// Component Name:  DateRangePicker component
// Author: Lavanya S
// Platform: Web
// OS: Windows
// IDE: Visual Studio Code
// Reference: Original implementation
// Date: 23-04-2025
// Description: Reusable date range picker component using React and Flatpickr.
//              It allows users to select a start date and an end date, with
//              configurable boundaries and callbacks.
// Dependencies: React, Flatpickr (via react-flatpickr), Redux (for warningToast)
// Interfaces: Accepts props including startDate, endDate, onChangeStart, onChangeEnd, startRef, endRef
// Maintenance History:  1.0 - First Version
import React, { useMemo, useCallback, useRef, useEffect } from "react";
import ReactFlatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { warningToast } from "../../store/dbFilter/actions";
import { connect } from "react-redux";

const isValidDate = (d) => d instanceof Date && !isNaN(d);
/**
 * DateRangePicker Component
 * Provides two date pickers for selecting a start and end date.
 * Uses ReactFlatpickr and supports Redux-based warning messages.
 *
 * @param {Date} startDate - Current selected start date.
 * @param {Date} endDate - Current selected end date.
 * @param {Function} onChangeStart - Callback function to update start date.
 * @param {Function} onChangeEnd - Callback function to update end date.
 * @param {Object} startRef - Ref for detecting clicks outside the start date picker.
 * @param {Object} endRef - Ref for detecting clicks outside the end date picker.
 */
const DateRangePicker = ({ startDate, endDate, onChangeStart, onChangeEnd, startRef, endRef,styleData={} }) => {
  const timeoutRef = useRef(null);

  // Memoized Flatpickr options for the start date picker
  const startOptions = useMemo(
    () => ({
      minDate: "2024-01-01",
      maxDate: new Date(),
      dateFormat: "Y-m-d",
    }),
    []
  );

  // Memoized Flatpickr options for the end date picker
  // Ensures end date cannot be before the start date
  const endOptions = useMemo(
    () => ({
      minDate: isValidDate(startDate) ? startDate : "2024-01-01",
      maxDate: new Date(),
      dateFormat: "Y-m-d",
    }),
    [startDate]
  );

  /**
   * Handles change in start date selection.
   * Triggers the `onChangeStart` callback.
   */
  const handleStartChange = useCallback(
    (selectedDates) => {
      if (selectedDates?.[0]) {
        onChangeStart(selectedDates[0]);
      }

      //   timeoutRef.current = setTimeout(() => {
      //     if (!endDate) {
      //       warningToast({ title: "Date Range Invalid", message: "Please select the End Date" });
      //     }
      //   }, 3000);
    },
    [onChangeStart]
  );

  /**
   * Handles change in end date selection.
   * Triggers the `onChangeEnd` callback.
   */
  const handleEndChange = useCallback(
    (selectedDates) => {
      if (selectedDates?.[0]) {
        onChangeEnd(selectedDates[0]);
      }

      //   timeoutRef.current = setTimeout(() => {
      //     if (!startDate) {
      //       warningToast({ title: "Date Range Invalid", message: "Please select the Start Date" });
      //     }
      //   }, 3000);
    },
    [onChangeEnd]
  );

  // Cleanup timeout when the component unmounts
  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const styles = {
  datePickerCustom: {
    width: "100px",
    outline: "none",
    border: `1.5pt solid #008CD7`,
    height: "56px",
    borderRadius: "4px",
    padding:"0 8px",
    ...styleData
  },
};
  return (
    <>
      {/* Start Date Picker */}
      <ReactFlatpickr
        value={isValidDate(startDate) ? startDate : ""}
        options={startOptions}
        placeholder="Start date"
        onChange={handleStartChange}
        style={styles.datePickerCustom}
        ref={startRef}
      />
      {/* End Date Picker */}
      <ReactFlatpickr
        value={isValidDate(endDate) ? endDate : ""}
        options={endOptions}
        placeholder="End date"
        onChange={handleEndChange}
        style={styles.datePickerCustom}
        ref={endRef}
      />
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  warningToast: (payload) => dispatch(warningToast(payload)),
});

export default React.memo(connect(null, mapDispatchToProps)(DateRangePicker));
